import React, { FC } from 'react';

import { ApolloQueryResult, gql } from '@apollo/client';
import { Loader, PublicError } from '@modernloop/shared/helper-components';

import {
  ActivityType,
  ActorType,
  ApplicationDetailsActivityLogsListQuery,
  useApplicationDetailsActivityLogsListQuery,
} from 'src/generated/mloop-graphql';

import { PAGER_DEFAULT } from 'src/constants';

import ActivityLogsList from '.';

interface ApplicationDetailsActivityLogsListProps {
  applicationId: string;
  activityTypes?: ActivityType[];
  actorTypes?: ActorType[];
}

const ApplicationDetailsActivityLogsList: FC<ApplicationDetailsActivityLogsListProps> = ({
  applicationId,
  activityTypes,
  actorTypes,
}) => {
  const { data, loading, error, fetchMore, updateQuery } = useApplicationDetailsActivityLogsListQuery({
    variables: {
      applicationId,
      input: {
        actorTypes,
        activityTypes,
        pageInput: {
          limit: PAGER_DEFAULT,
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  // If we are not careful we get the following error:
  //   > Expression produces a union type that is too complex
  // We are getting around that by casting to any.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let options = (data?.application?.activityLogs?.items ?? []) as any[];

  const loadMoreData = () => {
    if (!data?.application?.activityLogs?.nextCursor || loading || error || !fetchMore) {
      return;
    }

    // eslint-disable-next-line promise/catch-or-return
    fetchMore({
      variables: {
        input: {
          actorTypes,
          activityTypes,
          pageInput: {
            limit: PAGER_DEFAULT,
            cursor: data?.application?.activityLogs?.nextCursor,
          },
        },
      },
    }).then((res: ApolloQueryResult<ApplicationDetailsActivityLogsListQuery>) => {
      // If we are not careful we get the following error:
      //   > Expression produces a union type that is too complex
      // We are getting around that by casting to any.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newOptions = (res?.data?.application?.activityLogs?.items ?? []) as any[];

      // eslint-disable-next-line promise/always-return
      if (newOptions.length > 0) {
        options = [...options, ...newOptions];
      }

      updateQuery((prev: ApplicationDetailsActivityLogsListQuery) => {
        if (!res?.data?.application?.activityLogs?.items) {
          return prev;
        }

        return {
          application: {
            id: prev?.application?.id,
            activityLogs: {
              ...res?.data?.application.activityLogs,
              // If we are not careful we get the following error:
              //   > Expression produces a union type that is too complex
              // We are getting around that by casting to any.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              items: [...(prev?.application?.activityLogs?.items ?? []), ...newOptions] as any[],
              nextCursor: res?.data?.application.activityLogs.nextCursor,
            },
          },
        };
      });
    });
  };

  if (loading) {
    return <Loader loading />;
  }

  if (error) {
    return <PublicError error={error} />;
  }

  return (
    <ActivityLogsList
      activityLogs={options}
      onViewMoreClick={loadMoreData}
      showViewMoreButton={Boolean(data?.application?.activityLogs?.nextCursor)}
    />
  );
};

export default ApplicationDetailsActivityLogsList;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ApplicationDetailsActivityLogsListQueryGQL = gql`
  ${ActivityLogsList.fragments.activityLogs}
  fragment ApplicationDetailsActivityLogsList_activityLog on ActivityLog {
    id
    ...ActivityLogsList_activityLog
  }
  query ApplicationDetailsActivityLogsList(
    $applicationId: uuid!
    $input: ActivityLogsForSourceInput!
    $showTaskName: Boolean! = true
  ) {
    application(id: $applicationId) {
      id
      activityLogs(input: $input) {
        items {
          ...ApplicationDetailsActivityLogsList_activityLog
        }
        nextCursor
      }
    }
  }
`;
